import { cn } from 'cn';

import { Alert } from '../Alert';

type P = {
  readonly error?: Error;
  readonly errorMessage?: string;
  readonly shouldDisplayErrorDetails?: boolean;
} & ComponentStyling;

const formatErrorDetails = (error: Error) => {
  const details: Record<string, any> = {};

  if (error.name) {
    details.name = error.name;
  }

  if (error.message) {
    details.message = error.message;
  }

  if (error.cause) {
    details.cause = error.cause;
  }

  if (error.stack) {
    details.stack = error.stack;
  }

  // Handle HTTPError from ky library
  if ('response' in error && error.response) {
    const response = error.response as any;
    details.httpStatus = response.status;
    details.httpStatusText = response.statusText;
    details.httpUrl = response.url;
  }

  // Handle AbortError
  if (error instanceof DOMException && error.name === 'AbortError') {
    details.reason = 'Request was cancelled';
  }

  // Handle ZodError
  if ('errors' in error && Array.isArray((error as any).errors)) {
    details.validationErrors = (error as any).errors;
  }

  // Add any additional properties that might be on the error object
  Object.keys(error).forEach((key) => {
    if (!['name', 'message', 'cause', 'stack', 'response'].includes(key)) {
      details[key] = (error as any)[key];
    }
  });

  return details;
};

export const ErrorState = ({
  className,
  error,
  errorMessage = 'Ein Fehler ist aufgetreten', // TODO: i18n
  shouldDisplayErrorDetails,
  style,
}: P) => {
  const errorDetails = error ? formatErrorDetails(error) : null;

  return (
    <div
      className={cn('flex size-full items-center justify-center', className)}
      style={style}
    >
      <div className="max-w-2xl">
        <Alert severity="error">
          <div className="font-semibold">{errorMessage}</div>

          {shouldDisplayErrorDetails && errorDetails ? (
            <details className="mt-2 text-sm whitespace-pre-wrap">
              <summary className="cursor-pointer">Details</summary>
              <div className="mt-2 space-y-2">
                {errorDetails.httpStatus ? (
                  <div>
                    <span>HTTP Status:</span>
                    <div>
                      {errorDetails.httpStatus} {errorDetails.httpStatusText}
                    </div>
                  </div>
                ) : null}
                {errorDetails.httpUrl ? (
                  <div>
                    <span>URL:</span>
                    <div className="break-all">{errorDetails.httpUrl}</div>
                  </div>
                ) : null}
                {errorDetails.validationErrors ? (
                  <div>
                    <span>Validation Errors:</span>
                    <div>
                      {errorDetails.validationErrors.length} validation error(s)
                    </div>
                  </div>
                ) : null}
                {errorDetails.reason ? (
                  <div>
                    <span>Reason:</span>
                    <div>{errorDetails.reason}</div>
                  </div>
                ) : null}
                {errorDetails.message ? (
                  <div>
                    <span>Message:</span>
                    <div>{errorDetails.message}</div>
                  </div>
                ) : null}
                {errorDetails.name ? (
                  <div>
                    <span>Type:</span>
                    <div>{errorDetails.name}</div>
                  </div>
                ) : null}
                {Boolean(Object.keys(errorDetails).length) && (
                  <div>
                    <pre className="mt-1 max-h-40 overflow-auto p-2 text-xs whitespace-pre-wrap">
                      {JSON.stringify(errorDetails, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </details>
          ) : null}
        </Alert>
      </div>
    </div>
  );
};
